// Types of Tickable Mail documents (docs/tickable-mail.md §5). Plain TypeScript
// without imports: the dashboard imports this file as well.

export type Lang = "nl" | "en" | "de" | "fr";

// A translated text: the value per language. A translation may carry where it
// came from, so the editor can mark AI texts and texts whose source changed.
export type LText = string | { text: string; source_hash?: string; origin?: "ai" | "manual" };
export type L = Partial<Record<string, LText>>;

export type Block =
  | { id: string; type: "heading"; text: L; level?: 1 | 2 }
  | { id: string; type: "text"; html: L }
  | { id: string; type: "image"; src: string; alt: L; href?: string; width?: number; height?: number }
  | { id: string; type: "button"; label: L; href: string }
  | { id: string; type: "divider" }
  | { id: string; type: "spacer"; size?: "s" | "m" | "l" }
  | { id: string; type: "event" }
  // The fixed texts of these blocks can be overridden; empty = our text (strings.ts), so an
  // organizer who prefers "u" over "je" can say so. note: the line under the button.
  | { id: string; type: "tickets"; title?: L; label?: L; note?: L; pdf_label?: L }
  | { id: string; type: "payment_link"; title?: L; label?: L; note?: L }
  | { id: string; type: "order_link"; label?: L }
  // the extras still on sale for the order (order_extras), with a button to add them; empty = left out
  | { id: string; type: "extras"; title?: L; label?: L }
  | { id: string; type: "shop_button"; label?: L }
  // show_bought: also activities the recipient has tickets for (default no); show_waitlist: a sold
  // out activity with its waitlist on, linked as the waitlist (default yes)
  | { id: string; type: "event_promo"; mode: "upcoming" | "pick"; event_ids?: string[]; max?: number; title?: L; show_bought?: boolean; show_waitlist?: boolean }
  // "Persoonlijke code": at send every mail gets its own code of this discount that works once
  // (mail_send_discount_code), shown with a button that fills it in the shop (?discount=CODE);
  // left out where there is no code (a preview shows a sample)
  | { id: string; type: "discount_code"; discount_id: string; title?: L; label?: L; note?: L };

export type BlockType = Block["type"];

export interface MailDocument {
  version: 1;
  subject: L;
  preheader?: L;
  blocks: Block[];
}

export interface Brand {
  logo_url?: string | null;
  logo_dark_url?: string | null;
  // put the logo on a plate of this colour, so a dark logo survives dark mode
  logo_background?: string | null;
  accent?: string | null;
  background?: string | null;
  // the blocks on the background (white by default); dark with light text gives a dark mail
  card?: string | null;
  text?: string | null;
  font?: string | null;
}

// Brand order: the organizer's own mail brand (Instellingen > Mail), then the activity's own shop
// theme, then the organization's shop theme. A theme logo drops the dark-mode logo and plate, which
// belong to another logo.
export const eventBrand = (brand: Brand, own: Brand, theme: { main_color?: string | null; font?: string | null; logo_url?: string | null } | null | undefined): Brand => {
  if (!theme) return brand;
  return {
    ...brand,
    accent: own.accent || theme.main_color || brand.accent,
    font: own.font || theme.font || brand.font,
    ...(!own.logo_url && theme.logo_url ? { logo_url: theme.logo_url, logo_dark_url: null, logo_background: null } : {}),
  };
};

// Variables an organizer can use in texts. The key is what is stored in the
// document ({{buyer.first_name}}); the labels are what the editor shows.
export const MAIL_VARIABLES = [
  { key: "buyer.first_name", nl: "Voornaam", en: "First name" },
  { key: "buyer.name", nl: "Naam", en: "Name" },
  { key: "event.title", nl: "Evenement", en: "Event" },
  { key: "event.location", nl: "Locatie", en: "Location" },
  { key: "visit.date", nl: "Datum bezoek", en: "Visit date" },
  { key: "visit.time", nl: "Tijd bezoek", en: "Visit time" },
  { key: "visit.slot", nl: "Tijdslot", en: "Timeslot" },
  { key: "order.number", nl: "Bestelnummer", en: "Order number" },
  { key: "order.link", nl: "Link naar bestelling", en: "Order link" },
  { key: "order.pay_before", nl: "Betalen vóór", en: "Pay before" },
  { key: "organizer.name", nl: "Organisator", en: "Organizer" },
] as const;

export type MailVariable = typeof MAIL_VARIABLES[number]["key"];

// Blocks that need an order (they disappear from mails without one).
export const ORDER_BLOCKS: BlockType[] = ["tickets", "payment_link", "order_link", "extras"];

// How many activities an event_promo block shows: every picked one, else `max` of the upcoming.
export const PROMO_MAX = 10;
export const promoLimit = (block: { mode: "upcoming" | "pick"; max?: number }) =>
  block.mode === "pick" ? PROMO_MAX : Math.max(1, Math.min(block.max ?? 3, PROMO_MAX));

// A system mail must keep these blocks (docs §5).
export const REQUIRED_BLOCKS: Record<string, BlockType[]> = {
  order_confirmation: ["tickets"],
  reservation_payment: ["payment_link"],
  waitlist_offer: ["payment_link"],
};

export const textOf = (value: LText | undefined): string =>
  value == null ? "" : typeof value === "string" ? value : value.text ?? "";

// The text in a language, falling back to the source language, then to any.
export const pick = (l: L | undefined, lang: string, fallback: string): string => {
  if (!l) return "";
  const own = textOf(l[lang]);
  if (own.trim()) return own;
  const source = textOf(l[fallback]);
  if (source.trim()) return source;
  for (const value of Object.values(l)) {
    const text = textOf(value);
    if (text.trim()) return text;
  }
  return "";
};

// Stable, small hash of a source text, to see whether a translation is stale.
export const sourceHash = (text: string): string => {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};

// Every translatable string of a document with a path, for the translator.
export const collectTexts = (doc: MailDocument, lang: string): Record<string, string> => {
  const out: Record<string, string> = {};
  const add = (path: string, l?: L) => {
    const text = l ? textOf(l[lang]) : "";
    if (text.trim()) out[path] = text;
  };
  add("subject", doc.subject);
  add("preheader", doc.preheader);
  for (const block of doc.blocks) {
    switch (block.type) {
      case "heading": add(`${block.id}.text`, block.text); break;
      case "text": add(`${block.id}.html`, block.html); break;
      case "image": add(`${block.id}.alt`, block.alt); break;
      case "button": add(`${block.id}.label`, block.label); break;
      case "tickets":
        add(`${block.id}.title`, block.title);
        add(`${block.id}.label`, block.label);
        add(`${block.id}.note`, block.note);
        add(`${block.id}.pdf_label`, block.pdf_label);
        break;
      case "payment_link":
        add(`${block.id}.title`, block.title);
        add(`${block.id}.label`, block.label);
        add(`${block.id}.note`, block.note);
        break;
      case "order_link":
      case "shop_button": add(`${block.id}.label`, block.label); break;
      case "event_promo": add(`${block.id}.title`, block.title); break;
      case "discount_code":
        add(`${block.id}.title`, block.title);
        add(`${block.id}.label`, block.label);
        add(`${block.id}.note`, block.note);
        break;
      case "extras":
        add(`${block.id}.title`, block.title);
        add(`${block.id}.label`, block.label);
        break;
    }
  }
  return out;
};
